import React from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Fase 14.5 · Barra de reacciones con 5 tipos.
 *
 * Props:
 *   - targetType: "thread" | "post"
 *   - targetId: string
 *   - summary: { like, love, insightful, helpful, curious }
 *   - myReaction: string | null
 *   - onChange: (nextSummary, nextMyReaction) => void
 *   - disabled: bool (no permite interactuar)
 */
const REACTIONS = [
  { code: "like", icon: "👍", label: "Me gusta" },
  { code: "love", icon: "❤️", label: "Me encanta" },
  { code: "insightful", icon: "💡", label: "Perspicaz" },
  { code: "helpful", icon: "🎯", label: "Útil" },
  { code: "curious", icon: "🤔", label: "Curioso" },
];

export default function ReactionsBar({
  targetType, targetId, summary = {}, myReaction = null,
  onChange, disabled = false,
}) {
  const [busy, setBusy] = React.useState(null);

  const react = async (code) => {
    if (disabled || busy) return;
    setBusy(code);
    try {
      let resp;
      if (myReaction === code) {
        // toggle off
        resp = await api.delete(`/forums/${targetType}/${targetId}/reactions`);
      } else {
        resp = await api.post(`/forums/${targetType}/${targetId}/reactions`,
          { reaction: code });
      }
      onChange?.(resp.data.summary, resp.data.my_reaction);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={`forum-reactions-bar-${targetType}-${targetId}`}
      className="flex items-center gap-1 flex-wrap"
    >
      {REACTIONS.map((r) => {
        const count = Number(summary[r.code] || 0);
        const active = myReaction === r.code;
        return (
          <button
            key={r.code}
            type="button"
            title={r.label + (count ? ` · ${count}` : "")}
            disabled={disabled}
            onClick={() => react(r.code)}
            data-testid={`forum-reaction-btn-${r.code}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
              active
                ? "bg-brand-orange-500/20 border-brand-orange-500/50 text-brand-orange-500"
                : "bg-white/5 border-gray-700 text-gray-300 hover:bg-white/10 hover:text-white"
            } ${busy === r.code ? "opacity-60" : ""}`}
          >
            <span className="text-sm leading-none">{r.icon}</span>
            {count > 0 && <span className="font-mono">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
